
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import {
  Plus, 
  FileEdit, 
  Trash, 
  Loader2, 
  X,
  User,
  Users,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { categoriesAPI } from "@/services/api";
import { format } from "date-fns";
import ModernPageTemplate, { 
  ModernBadge, 
  ModernLoading,
  ModernPaginationFooter
} from "@/components/common/ModernPageTemplate";

export default function Categories() {
  const CATEGORIES_PER_PAGE = 5;
  
  // Main state management
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("my"); // Start with "my" instead of "all"
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(CATEGORIES_PER_PAGE);
  const [isPaginationEnabled, setIsPaginationEnabled] = useState(true);
  
  // Sorting states
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Modal and form states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [newSubCategory, setNewSubCategory] = useState("");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Effect to adjust footer position based on sidebar state
  useEffect(() => {
    const adjustFooterPosition = () => {
      const footer = document.querySelector('.admin-fixed-footer');
      if (!footer) return;

      // Check if we're on desktop (lg breakpoint)
      if (window.innerWidth >= 1024) {
        // Look for the main content container to determine sidebar state
        const mainContent = document.querySelector('[class*="lg:pl-"]');
        if (mainContent) {
          const classes = mainContent.className;
          if (classes.includes('lg:pl-14')) {
            // Sidebar is collapsed (68px width)
            footer.style.left = '68px';
          } else if (classes.includes('lg:pl-64')) {
            // Sidebar is expanded (256px width)
            footer.style.left = '256px';
          }
        }
      } else {
        // Mobile/tablet - full width
        footer.style.left = '0px';
      }
    };

    // Initial adjustment
    adjustFooterPosition();

    // Listen for window resize
    window.addEventListener('resize', adjustFooterPosition);

    // Use MutationObserver to watch for class changes on the main content
    const observer = new MutationObserver(adjustFooterPosition);
    const mainContent = document.querySelector('[class*="lg:pl-"]');
    if (mainContent) {
      observer.observe(mainContent, { 
        attributes: true, 
        attributeFilter: ['class'] 
      });
    }

    return () => {
      window.removeEventListener('resize', adjustFooterPosition);
      observer.disconnect();
    };
  }, []);

  const form = useForm({
    defaultValues: {
      name: "",
      description: "",
      active: true,
    }
  });

  const editForm = useForm({
    defaultValues: {
      name: "",
      description: "",
      active: true,
    }
  });

  // Query to get categories data with unified pagination
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['categories', currentPage, rowsPerPage, searchTerm, activeTab, sortField, sortOrder],
    queryFn: () => {
      const params = {
        page: currentPage, 
        limit: rowsPerPage, 
        filterType: activeTab,
        sortField: sortField,
        sortOrder: sortOrder
      };
      
      // Add search parameters based on active tab
      if (activeTab === 'my') {
        params.mySearch = searchTerm;
        params.myPage = currentPage;
        params.myLimit = rowsPerPage;
      } else if (activeTab === 'company') {
        params.companySearch = searchTerm;
        params.companyPage = currentPage;
        params.companyLimit = rowsPerPage;
      }
      
      return categoriesAPI.getAllCat(params);
    },
    keepPreviousData: true,
  });

  // ... keep existing code (mutations)
  const createMutation = useMutation({
    mutationFn: (newCategory) => categoriesAPI.create(newCategory),
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      setIsAddModalOpen(false);
      toast({
        title: "Success",
        description: "Category created successfully",
      });
      resetForm();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to create category",
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => categoriesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['categories']);
      setIsEditModalOpen(false);
      setCurrentCategory(null);
      toast({
        title: "Success",
        description: "Category updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update category",
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => categoriesAPI.delete(id),
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Category deleted successfully",
      });
      
      queryClient.invalidateQueries(['categories']);
      setIsDeleteConfirmOpen(false);
      setCategoryToDelete(null);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete category",
        variant: "destructive",
      });
    },
  });

  // Form population for edit
  useEffect(() => {
    if (currentCategory) {
      editForm.setValue("name", currentCategory.name || "");
      editForm.setValue("description", currentCategory.description || "");
      editForm.setValue("active", currentCategory.active !== false);
      
      const subCategoryNames = currentCategory.subCategories?.map(sub => 
        typeof sub === 'string' ? sub : sub.name
      ) || [];
      setSubCategories(subCategoryNames);
    }
  }, [currentCategory, editForm]);

  // Search and tab handlers
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Reset page when search term changes
  useEffect(() => {
    setCurrentPage(1);
    // Clear sorting when search changes to show newest first
    setSortField('');
    setSortOrder('asc');
  }, [searchTerm]);

  const handleRefresh = async () => {
    await refetch();
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    setCurrentPage(1); // Reset to first page when changing tabs
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  const handlePaginationToggle = () => {
    setIsPaginationEnabled(!isPaginationEnabled);
  };

  // Sorting handlers
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1" />;
    return sortOrder === 'asc' ? (
      <ArrowUp className="h-3 w-3 ml-1" />
    ) : (
      <ArrowDown className="h-3 w-3 ml-1" />
    );
  };

  // Helper functions
  const resetForm = () => {
    form.reset();
    setSubCategories([]);
    setNewSubCategory("");
  };

  const handleAddSubCategory = () => {
    if (newSubCategory.trim() && !subCategories.includes(newSubCategory.trim())) {
      setSubCategories([...subCategories, newSubCategory.trim()]);
      setNewSubCategory("");
    }
  };

  const handleRemoveSubCategory = (index) => {
    const newSubCategories = [...subCategories];
    newSubCategories.splice(index, 1);
    setSubCategories(newSubCategories);
  };

 const onSubmit = (formData) => {
    // Trim form data
    const trimmedData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      active: formData.active,
      subCategories: subCategories.map(sub => sub.trim()).filter(sub => sub.length > 0)
    };
    createMutation.mutate(trimmedData);
  };

  const onEditSubmit = (formData) => {
    // Trim form data
    const trimmedData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      active: formData.active,
      subCategories: subCategories.map(sub => sub.trim()).filter(sub => sub.length > 0)
    };
    
    updateMutation.mutate({
      id: currentCategory._id,
      data: trimmedData
    });
  };

  const handleEdit = (category) => {
    setCurrentCategory(category);
    setIsEditModalOpen(true);
  };

  const handleDeleteCategory = (category) => {
    setCategoryToDelete(category);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (categoryToDelete) {
      deleteMutation.mutate(categoryToDelete._id);
    }
  };

  // Render main categories table
  const renderCategoriesTable = () => {
    if (isLoading) {
      return <ModernLoading />;
    }

    if (isError) {
      return (
        <div className="text-center p-6 text-red-500 border rounded-md">
          Failed to load categories. Please try again.
        </div>
      );
    }

    // Get categories based on active tab
    let categories = [];
    let totalCount = 0;

    if (data?.data?.data) {
      const { userCategories, companyCategories } = data.data.data;
      
      switch (activeTab) {
        case "my":
          categories = userCategories?.data || [];
          totalCount = userCategories?.pagination?.total || 0;
          break;
        case "company":
          categories = companyCategories?.data || [];
          totalCount = companyCategories?.pagination?.total || 0;
          break;
        default:
          categories = userCategories?.data || [];
          totalCount = userCategories?.pagination?.total || 0;
          break;
      }
    }

    // Apply client-side sorting if sortField is set
    if (sortField && categories.length > 0) {
      categories = [...categories].sort((a, b) => {
        let aValue, bValue;

        // Handle nested properties
        if (sortField.includes('.')) {
          const fields = sortField.split('.');
          aValue = fields.reduce((obj, field) => obj?.[field], a);
          bValue = fields.reduce((obj, field) => obj?.[field], b);
        } else {
          aValue = a[sortField];
          bValue = b[sortField];
        }

        // Handle date fields
        if (sortField === 'createdDate') {
          aValue = aValue ? new Date(aValue) : new Date(0);
          bValue = bValue ? new Date(bValue) : new Date(0);
        }

        // Handle string fields
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return sortOrder === 'asc' ? 1 : -1;
        if (bValue == null) return sortOrder === 'asc' ? -1 : 1;

        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }

    if (!categories || categories.length === 0) {
      return (
        <div className="text-center p-6 text-gray-500 border rounded-md">
          No categories found
        </div>
      );
    }

    return (
      <div className="admin-table-container">
        <table className="admin-table">
          <thead className="admin-table-header">
            <tr>
              <th className="admin-table-cell">S.No</th>
              <th
                className="admin-table-cell sortable"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center cursor-pointer">
                  Name
                  {getSortIcon('name')}
                </div>
              </th>
              <th
                className="admin-table-cell sortable"
                onClick={() => handleSort('description')}
              >
                <div className="flex items-center cursor-pointer">
                  Description
                  {getSortIcon('description')}
                </div>
              </th>
              <th className="admin-table-cell">Subcategories</th>
              <th
                className="admin-table-cell sortable"
                onClick={() => handleSort('active')}
              >
                <div className="flex items-center cursor-pointer">
                  Status
                  {getSortIcon('active')}
                </div>
              </th>
              <th
                className="admin-table-cell sortable"
                onClick={() => handleSort('createdBy.name')}
              >
                <div className="flex items-center cursor-pointer">
                  Created By
                  {getSortIcon('createdBy.name')}
                </div>
              </th>
              <th
                className="admin-table-cell sortable"
                onClick={() => handleSort('createdDate')}
              >
                <div className="flex items-center cursor-pointer">
                  Created On
                  {getSortIcon('createdDate')}
                </div>
              </th>
              <th className="admin-table-cell text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category, index) => (
              <tr key={category._id} className="admin-table-row">
                <td className="admin-table-cell number">
                  {(currentPage - 1) * rowsPerPage + index + 1}
                </td>
                <td className="admin-table-cell font-medium">
                  <div className="flex items-center">
                    <div className="min-w-0">
                      <div className="text-sm font-medium" title={category.name}>
                        {category.name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="admin-table-cell">
                  <div className="text-sm" title={category.description}>
                    {category.description || "N/A"}
                  </div>
                </td>
                <td className="admin-table-cell">
                  <div className="flex flex-wrap gap-1">
                    {category.subCategories && category.subCategories.length > 0 ? (
                      <>
                        {category.subCategories.slice(0, 2).map((sub, index) => (
                          <ModernBadge key={index} variant="language" className="text-xs">
                            {typeof sub === 'string' ? sub : sub.name}
                          </ModernBadge>
                        ))}
                        {category.subCategories.length > 2 && (
                          <ModernBadge variant="template" className="text-xs">
                            +{category.subCategories.length - 2}
                          </ModernBadge>
                        )}
                      </>
                    ) : (
                      <span className="text-xs text-gray-500">None</span>
                    )}
                  </div>
                </td>
                <td className="admin-table-cell">
                  <ModernBadge variant={category.active ? "success" : "secondary"} className="text-xs">
                    {category.active ? "Active" : "Inactive"}
                  </ModernBadge>
                </td>
                <td className="admin-table-cell">
                  <span className="text-xs">{category.createdBy?.name || "N/A"}</span>
                </td>
                <td className="admin-table-cell">
                  <span className="text-xs">
                    {category.createdDate ? format(new Date(category.createdDate), 'MMM dd, yy') : 'N/A'}
                  </span>
                </td>
                <td className="admin-table-cell text-right">
                  <div className="admin-table-actions">
                    <button
                      className="admin-table-action-btn edit"
                      onClick={() => handleEdit(category)}
                      title="Edit category"
                    >
                      <FileEdit className="h-3 w-3" />
                    </button>
                    <button
                      className="admin-table-action-btn delete"
                      onClick={() => handleDeleteCategory(category)}
                      title="Delete category"
                    >
                      <Trash className="h-3 w-3" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Get total count for stats
  const getTotalCount = () => {
    if (!data?.data?.data) return 0;
    const { userCategories, companyCategories } = data.data.data;
    
    switch (activeTab) {
      case "my":
        return userCategories?.pagination?.total || 0;
      case "company":
        return companyCategories?.pagination?.total || 0;
      default:
        return userCategories?.pagination?.total || 0;
    }
  };

  return (
    <ModernPageTemplate
      title="Categories"
      contentClassName="categories-page"
      stats={{
        total: getTotalCount(),
        customStats: (
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList className="categories-header-tabs-list">
              <TabsTrigger value="my" className="categories-header-tab">
                <User className="h-3 w-3" />
                My Categories
              </TabsTrigger>
              <TabsTrigger value="company" className="categories-header-tab">
                <Users className="h-3 w-3" />
                Company Categories
              </TabsTrigger>
            </TabsList>
          </Tabs>
        )
      }}
      onRefresh={handleRefresh}
      search={{
        placeholder: "Search categories by name or description...",
        value: searchTerm,
        onChange: handleSearchChange,
        onSubmit: (e) => e.preventDefault()
      }}
      createAction={{
        onClick: () => setIsAddModalOpen(true),
        tooltip: "Add Category"
      }}
    >
      {renderCategoriesTable()}

      {/* Reusable Pagination Footer */}
      <ModernPaginationFooter
        isPaginationEnabled={isPaginationEnabled}
        onPaginationToggle={handlePaginationToggle}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        pagination={{
          current: currentPage,
          pages: Math.ceil(getTotalCount() / rowsPerPage) || 1,
          total: getTotalCount()
        }}
        onPageChange={handlePageChange}
        rowsOptions={[5, 10, 20, 50]}
      />

      {/* ... keep existing code (Add Category Dialog, Edit Category Dialog) */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add New Category</DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                rules={{ required: "Category name is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter category name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter category description" 
                        className="resize-none" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <FormLabel>Subcategories</FormLabel>
                <div className="flex space-x-2">
                  <Input
                    placeholder="Enter subcategory"
                    value={newSubCategory}
                    onChange={(e) => setNewSubCategory(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSubCategory();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button type="button" onClick={handleAddSubCategory} size="icon">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2 mt-2">
                  {subCategories.map((subCategory, index) => (
                    <ModernBadge key={index} variant="secondary" className="flex items-center gap-1">
                      {subCategory}
                      <button 
                        type="button" 
                        onClick={() => handleRemoveSubCategory(index)}
                        className="hover:text-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </ModernBadge>
                  ))}
                  {subCategories.length === 0 && (
                    <p className="text-sm text-gray-500">No subcategories added yet</p>
                  )}
                </div>
              </div>

              <FormField
                control={form.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Status</FormLabel>
                      <div className="text-sm text-gray-500">
                        Set the category as active or inactive
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setIsAddModalOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createMutation.isLoading}>
                  {createMutation.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Add Category
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={isEditModalOpen} onOpenChange={(open) => {
        setIsEditModalOpen(open);
        if (!open) {
          setCurrentCategory(null);
          setSubCategories([]);
        }
      }}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          <Form {...editForm}>
            <form onSubmit={editForm.handleSubmit(onEditSubmit)} className="space-y-4">
              <FormField
                control={editForm.control}
                name="name"
                rules={{ required: "Category name is required" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter category name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={editForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Enter category description" 
                        className="resize-none" 
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <FormLabel>Subcategories</FormLabel>
                <div className="flex space-x-2">
                  <Input
                    placeholder="Enter subcategory"
                    value={newSubCategory}
                    onChange={(e) => setNewSubCategory(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSubCategory();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button type="button" onClick={handleAddSubCategory} size="icon">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2 mt-2">
                  {subCategories.map((subCategory, index) => (
                    <ModernBadge key={index} variant="secondary" className="flex items-center gap-1">
                      {subCategory}
                      <button 
                        type="button" 
                        onClick={() => handleRemoveSubCategory(index)}
                        className="hover:text-red-500"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </ModernBadge>
                  ))}
                  {subCategories.length === 0 && (
                    <p className="text-sm text-gray-500">No subcategories added yet</p>
                  )}
                </div>
              </div>

              <FormField
                control={editForm.control}
                name="active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Status</FormLabel>
                      <div className="text-sm text-gray-500">
                        Set the category as active or inactive
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setCurrentCategory(null);
                    setSubCategories([]);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={updateMutation.isLoading}>
                  {updateMutation.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Update Category
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Category</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{categoryToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ModernPageTemplate>
  );
}
